import TWEEN from '@tweenjs/tween.js';
import {runIncremental} from './runIncremental';
import {AnimationPair, PulseParams} from './types';

// Default pulse curve: sin²(π·t). Satisfies curve(0) = curve(1) = 0 and
// peaks at 1 when t = 0.5, so the excursion grows and shrinks smoothly within
// a single open (or close) phase and self‑cancels on completion.
const defaultPulseCurve = (t: number): number => {
    const s = Math.sin(Math.PI * t);
    return s * s;
};

// 'pulse' strategy — "excursion and retract."
//
// Adds a curve‑shaped transient offset on top of the live axis value using
// the shared incremental‑additive engine, and removes exactly what it added
// on completion. The final net contribution is 0, so the axis returns to
// whatever resting position other writers had established — this is what
// makes the pulse safe to layer on top of a mesh whose absolute placement
// may still be finalising (e.g. an attach‑time close snap that runs after
// the tween started).
export const pulseStrategy = (params: PulseParams): AnimationPair => {
    const {
        mesh,
        property,
        axis,
        openDelay = 0,
        closeDelay = 0,
        openDuration = 1000,
        closeDuration = 1000,
    } = params;

    const curve = params.curve ?? defaultPulseCurve;

    const runPulse = (duration: number, delayBeforeStart: number) => {
        const amplitude = params.getAmplitude();
        if (amplitude === 0) {
            return Promise.resolve();
        }
        // Linear easing on progress: the excursion's shape lives entirely in
        // `curve`, keeping the curve contract (curve(0) = curve(1) = 0)
        // independent of any TWEEN easing remap.
        return runIncremental({
            mesh,
            property,
            axis,
            duration,
            delayBeforeStart,
            easing: TWEEN.Easing.Linear.None,
            progressToOffset: (progress) => amplitude * curve(progress),
            finalOffset: 0,
        });
    };

    return {
        open: (duration = openDuration) => runPulse(duration, openDelay),
        close: (duration = closeDuration) => runPulse(duration, closeDelay),
    };
};
